import { memo, useCallback, useContext, useState } from 'react';
import { flip, useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import { cx } from '@trinity/chat-components/utils';
import { Focusable } from '../Focusable';
import { Portal } from '../Portal';
import { useSameWidthMiddleware, useZIndexMiddleware } from '../presentation/popper-modifiers';
import type { ItemProps, ItemsProps, IDropDownItem, Props, SelectionProps } from './interfaces';
import styles from './style.module.css';

const Item = memo(<T extends IDropDownItem>({ onSelect, ...props }: ItemProps<T>) => {
  const theming = useContext(AppThemingContext);
  const handleMouseDown = useCallback((item: T) => () => {
    onSelect(item);
  }, [onSelect]);

  // Matches PopperMenuItem's hover theming (primary.lighter for the background,
  // primary.dark for the text), so menu-style pickers stay visually consistent.
  return (
    <div
      className={styles.item}
      style={{
        '--theme-color-hover': theming?.enabled ? theming.palette?.primary?.lighter : undefined,
        '--theme-text-hover': theming?.enabled ? theming.palette?.primary?.dark : undefined,
      } as React.CSSProperties}
      onMouseDown={handleMouseDown(props.item)}>
      {props.value}
    </div>
  );
});

const Items = memo(<T extends IDropDownItem>(props: ItemsProps<T>) => {

  return (
    <div className={styles.list}>
      {props.items.map(item =>
        <Item
          item={item}
          key={item.id}
          onSelect={props.onSelect}
          value={props.getItemValue(item)} />,
      )}
    </div>
  );
});

const Selection = <T extends IDropDownItem>({
  classes = {},
  ...props
}: SelectionProps<T>) => {
  const textClass = cx(styles.selectedText, classes.text);
  return (
    <Focusable autoFocus={props.autoFocus} disabled>
      <div
        className={cx(styles.input, {
          [styles.disabled]: props.disabled,
        })}
        tabIndex={0}
        onClick={props.toggleVisibility}>
        <div className={textClass}>
          {props.text}
        </div>
        <div className={styles.arrowDown} />
      </div>
    </Focusable>
  );
};

export const DropDown = <T extends IDropDownItem>({
  classes = {},
  items = [],
  onSelect,
  placeholder,
  sameWidth = true,
  ...props
}: Props<T>) => {
  const [open, setOpen] = useState(false);

  const [referenceElement, setReferenceElement] = useState<HTMLElement>(null);

  const toggleVisibility = useCallback(() => {
    if (props.disabled) return;

    setOpen(true);
  }, [props.disabled]);

  const handleBlur = useCallback((e: MouseEvent) => {
    if (!referenceElement.contains(e.target as Node)) {
      setOpen(false);
    }
  }, [referenceElement]);

  const handleSelection = useCallback((data: T) => {
    onSelect(data);
    setOpen(false);
  }, [onSelect, setOpen]);

  const sameWidthMiddleware = useSameWidthMiddleware();
  const zIndexMiddleware = useZIndexMiddleware();

  const { refs, floatingStyles } = useFloating({
    elements: {
      reference: referenceElement,
    },
    middleware: [
      ...(sameWidth ? [sameWidthMiddleware] : []),
      zIndexMiddleware,
      flip({ fallbackPlacements: ['top'] }),
    ],
    placement: 'bottom-start',
  });

  const selectionTextClassName = cx(classes.text, {
    [styles.placeholder]: !props.text && !!placeholder,
  });

  return (
    <>
      <div
        ref={setReferenceElement}
        className={cx(styles.root, classes.root)}>
        <Selection
          autoFocus={props.autoFocus}
          disabled={props.disabled}
          open={open}
          text={props.text || placeholder}
          classes={{ text: selectionTextClassName }}
          toggleVisibility={toggleVisibility} />
      </div>
      {open &&
        <Portal>
          <ClickAwayListener
            mouseEvent='onMouseDown'
            onClickAway={handleBlur}
            touchEvent='onTouchStart'>
            <div
              className={styles.popper}
              ref={refs.setFloating}
              style={floatingStyles}>
              <Items
                getItemValue={props.getItemValue}
                items={items}
                onSelect={handleSelection} />
            </div>
          </ClickAwayListener>
        </Portal>
      }
    </>
  );
};

export default DropDown;